/**
 * Unified Admin API Client
 * Handles authentication, error handling, and response parsing for all admin endpoints.
 * Auth uses httpOnly cookie (set by backend) as primary, with localStorage fallback.
 */
import { BACKEND_URL } from '../config';
import toast from './toast';

/**
 * Get the admin token from localStorage (fallback for legacy sessions)
 */
const getAdminToken = () => {
  return localStorage.getItem('admin_token');
};

/**
 * Make an authenticated admin API request.
 * The httpOnly cookie is sent automatically via credentials: 'include'.
 * localStorage token is sent as Authorization header for backwards compatibility.
 */
export const adminApiFetch = async (endpoint, options = {}) => {
  const token = getAdminToken();
  const url = `${BACKEND_URL}${endpoint}`;
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  // Include Authorization header for backwards compat (httpOnly cookie is primary)
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    credentials: 'include',
    headers,
    ...options,
  };

  try {
    const response = await fetch(url, config);
    const contentType = response.headers.get('content-type') || '';

    if (!response.ok) {
      let errorMessage = `Request failed with status ${response.status}`;

      if (response.status === 401 || response.status === 403) {
        toast.error('Your admin session has expired. Please log in again.');
        localStorage.removeItem('admin_token');
        window.location.href = '/admin/login';
        throw new Error('Unauthorized');
      }

      if (contentType.includes('application/json')) {
        try {
          const errorJson = await response.json();
          errorMessage = errorJson.detail || errorJson.message || errorMessage;
        } catch (e) {
          // JSON parse failed
        }
      }

      throw new Error(errorMessage);
    }

    if (!contentType.includes('application/json')) {
      throw new Error('Unexpected response format from server');
    }

    return await response.json();
  } catch (error) {
    if (error.message === 'Unauthorized') {
      throw error;
    }
    throw error;
  }
};

/**
 * GET request helper
 */
export const adminApiGet = async (endpoint, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = queryString ? `${endpoint}?${queryString}` : endpoint;
  return adminApiFetch(url, { method: 'GET' });
};

/**
 * POST request helper
 */
export const adminApiPost = async (endpoint, body = {}) => {
  return adminApiFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

/**
 * PATCH request helper
 */
export const adminApiPatch = async (endpoint, body = {}) => {
  return adminApiFetch(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

/**
 * PUT request helper
 */
export const adminApiPut = async (endpoint, body = {}) => {
  return adminApiFetch(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

/**
 * DELETE request helper
 */
export const adminApiDelete = async (endpoint) => {
  return adminApiFetch(endpoint, { method: 'DELETE' });
};

/**
 * Admin logout - clears httpOnly cookie and localStorage
 */
export const adminLogout = async () => {
  try {
    await fetch(`${BACKEND_URL}/api/admin/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (e) {
    // Best effort
  }
  localStorage.removeItem('admin_token');
  window.location.href = '/admin/login';
};
